/**
 * Canonical product-telemetry event taxonomy — Pack 13.J.
 *
 * Owning task: Task #1106.
 *
 * Mirrors the discipline of `shared/audit-events.ts`: a single, code-side
 * registry that both the client emitter and the server ingester reference.
 * Adding a new event REQUIRES a row here AND an instrument-site under
 * `client/src/`; events not present in the registry are dropped at
 * ingest. The contract is asserted by
 * `client/src/lib/__tests__/telemetry.test.ts`.
 *
 * Categories
 *   - "navigation"  — page views, route transitions
 *   - "interaction" — primary-action clicks (buttons, submits)
 *   - "funnel"      — explicit funnel-step markers for conversion analysis
 *   - "feature"     — feature flag evaluations
 *   - "lifecycle"   — session boundaries (start/end), opt-out toggles
 *
 * PII discipline
 *   - `tenantId` and `actorRoleBucket` are the ONLY identifying fields.
 *   - `properties` MUST be a `Record<string, string|number|boolean|null>`
 *     of opaque tokens (template IDs, durations, step names). User
 *     identifiers, email addresses, names, free-text descriptions, and
 *     raw query strings are stripped at the emit site.
 */

export type TelemetryCategory =
  | "navigation"
  | "interaction"
  | "funnel"
  | "feature"
  | "lifecycle";

export interface TelemetryEventDef {
  /** Canonical event key (snake_case, dot-namespaced). Stable contract. */
  key: string;
  /** Human-readable label for the SA usage dashboard. */
  label: string;
  /** One-line description of what the event represents. */
  description: string;
  category: TelemetryCategory;
  /**
   * For funnel events, the funnel slug they belong to (so the
   * dashboard can stitch them into a conversion ladder).
   */
  funnelSlug?: string;
  /** Optional ordering within a funnel (1 = first step). */
  funnelStep?: number;
}

/**
 * Canonical funnel definitions referenced by `funnelSlug`.
 * Each funnel is an ordered ladder of telemetry event keys.
 */
export interface TelemetryFunnelDef {
  slug: string;
  label: string;
  steps: { eventKey: string; label: string }[];
}

export const TELEMETRY_EVENT_TAXONOMY: TelemetryEventDef[] = [
  // ─── lifecycle ────────────────────────────────────────────────────
  {
    key: "app.session_start",
    label: "Session started",
    description:
      "A new browser session started (page load with no prior session id). Anonymous; no tenant context required.",
    category: "lifecycle",
  },
  {
    key: "app.identified",
    label: "Actor identified",
    description:
      "Actor's tenant + role bucket became known after login. Properties carry only the role bucket.",
    category: "lifecycle",
  },
  {
    key: "app.opt_out_changed",
    label: "Telemetry opt-out toggled",
    description:
      "An admin toggled per-tenant telemetry opt-out. Properties.enabled is the new state.",
    category: "lifecycle",
  },

  // ─── navigation ───────────────────────────────────────────────────
  {
    key: "app.page_view",
    label: "Page view",
    description:
      "User navigated to a route. Properties.surface is the route template (parameterised IDs replaced with `:id`).",
    category: "navigation",
  },
  {
    key: "app.alias_redirect",
    label: "Legacy URL alias redirect",
    description:
      "User landed on a deprecated client-side URL alias and was redirected to the canonical route. Properties: { from, to }. Drives the PTW alias sunset schedule (Task #1255).",
    category: "navigation",
  },

  // ─── feature ──────────────────────────────────────────────────────
  {
    key: "feature.flag_evaluated",
    label: "Feature flag evaluated",
    description:
      "A feature flag (FEATURE_CATALOG entitlement OR Unleash rollout flag) was evaluated for the actor. Properties: { flag, enabled, source }.",
    category: "feature",
  },

  // ─── interaction (primary actions) ────────────────────────────────
  {
    key: "ui.primary_action",
    label: "Primary action click",
    description:
      "A user clicked a primary action button (data-telemetry-action). Properties.action is the canonical action name; Properties.surface is the route.",
    category: "interaction",
  },

  // ─── funnel: onboarding completion ────────────────────────────────
  {
    key: "onboarding.started",
    label: "Onboarding started",
    description: "Worker entered the onboarding flow (step 1 surfaced).",
    category: "funnel",
    funnelSlug: "worker_onboarding",
    funnelStep: 1,
  },
  {
    key: "onboarding.identity_verified",
    label: "Onboarding — identity verified",
    description: "Worker reached the identity-verified step.",
    category: "funnel",
    funnelSlug: "worker_onboarding",
    funnelStep: 2,
  },
  {
    key: "onboarding.documents_uploaded",
    label: "Onboarding — documents uploaded",
    description: "Worker uploaded mandatory onboarding documents.",
    category: "funnel",
    funnelSlug: "worker_onboarding",
    funnelStep: 3,
  },
  {
    key: "onboarding.completed",
    label: "Onboarding completed",
    description: "Worker reached the completion screen of the onboarding flow.",
    category: "funnel",
    funnelSlug: "worker_onboarding",
    funnelStep: 4,
  },

  // ─── funnel: permit creation ──────────────────────────────────────
  {
    key: "permit.create_started",
    label: "Permit creation started",
    description: "Operator opened the new-permit form.",
    category: "funnel",
    funnelSlug: "permit_creation",
    funnelStep: 1,
  },
  {
    key: "permit.create_template_selected",
    label: "Permit template selected",
    description: "Operator picked a permit template / hazard class.",
    category: "funnel",
    funnelSlug: "permit_creation",
    funnelStep: 2,
  },
  {
    key: "permit.create_submitted",
    label: "Permit submitted",
    description: "Operator submitted the new permit for review.",
    category: "funnel",
    funnelSlug: "permit_creation",
    funnelStep: 3,
  },
  {
    key: "permit.create_issued",
    label: "Permit issued",
    description: "Permit reached `issued` status (HSE + final approver signed off).",
    category: "funnel",
    funnelSlug: "permit_creation",
    funnelStep: 4,
  },

  // ─── interaction / lifecycle: SIMOPS conflict trend ──────────────
  // PTW Phase 7.B (Task #1276). Three events stitch the SIMOPS conflict
  // lifecycle together so the Phase 11 dashboards can compute:
  //   - rolling 7d/30d conflict-detection volume per tenant + scope
  //   - reviewer-engagement (board_viewed / detected ratio)
  //   - mitigation-acceptance ratio + median time-to-accept
  // No PII is carried — only the conflict count, scope, severity bucket,
  // and an opaque review id (UUID, not the human permit number).
  {
    key: "simops.conflict_detected",
    label: "SIMOPS conflict detected",
    description:
      "A new SIMOPS conflict was detected during permit creation and a review record was auto-created. Properties: { reviewId, conflictCount, scope, severity }.",
    category: "lifecycle",
  },
  {
    key: "simops.board_viewed",
    label: "SIMOPS board viewed",
    description:
      "A reviewer opened the SIMOPS visual board (timeline or map view). Properties: { view: 'timeline'|'map'|'list', reviewCount }.",
    category: "navigation",
  },
  {
    key: "simops.accepted_with_mitigation",
    label: "SIMOPS accepted with mitigation",
    description:
      "An Area Authority accepted the SIMOPS conflict with a captured mitigation note. Properties: { reviewId, conflictCount, noteLength }. Drives the Phase 11 conflict-trend dashboard's mitigation-ratio chart.",
    category: "interaction",
  },

  // ─── reports integration: PTW Phase 9.C (Task #1286) ─────────────
  // Outbound events emitted by `server/engines/ptw-reports-sink.ts`.
  // PTW emits structured events to the reports surface through a typed
  // envelope mirroring the Phase 9.A integration-signal contract. Each
  // event is opaque — no PII, no free-text. Properties:
  //   permitId   (opaque uuid)
  //   permitType (canonical work_type bucket: hot_work, confined_space, …)
  //   plus event-specific opaque tokens (signal name, outcome bucket).
  // Drives the Phase 11 dashboards' "PTW report integration health" tile
  // and the integration-vs-fallback matrix gate in CI.
  {
    key: "ptw.report.permit_issued",
    label: "PTW report — permit issued",
    description:
      "A permit reached `issued` and the reports module accepted the structured event. Properties: { permitId, permitType }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_closed",
    label: "PTW report — permit closed",
    description:
      "A permit reached a terminal closed state and the reports module accepted the structured event. Properties: { permitId, permitType, closeReason }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.signal_evaluated",
    label: "PTW report — integration signal evaluated",
    description:
      "A Phase 9.A/9.B integration signal was evaluated for a permit and the outcome was recorded against the reports surface. Properties: { permitId, permitType, signal, integrated, ok, primaryReason }. Drives the integration-vs-fallback matrix dashboard.",
    category: "lifecycle",
  },

  // ─── reports integration: PTW Phase 11.A (Task #1290) ────────────
  // Phase 11.A widens the `ptw.report.*` namespace to cover every
  // typed lifecycle event called out in master plan §5.2 plus the
  // lifecycle-funnel markers needed by the operator/leadership/HSE
  // analytics dashboards. Each row is opaque (no PII, no free text);
  // properties carry only canonical bucket tokens (reason codes,
  // hazard class, signal name, elapsed-millis, etc.).
  {
    key: "ptw.report.permit_created",
    label: "PTW report — permit created",
    description:
      "A new permit row was created (lifecycle funnel: step 1). Properties: { permitId, permitType, hazardClass, siteId? }.",
    category: "funnel",
    funnelSlug: "ptw_lifecycle",
    funnelStep: 1,
  },
  {
    key: "ptw.report.permit_submitted",
    label: "PTW report — permit submitted for review",
    description:
      "Permit was submitted into the review/approval ladder (lifecycle funnel: step 2). Properties: { permitId, permitType, hazardClass }.",
    category: "funnel",
    funnelSlug: "ptw_lifecycle",
    funnelStep: 2,
  },
  {
    key: "ptw.report.permit_active",
    label: "PTW report — permit went active",
    description:
      "Permit transitioned from `issued` to `active` (work commenced, lifecycle funnel: step 4). Properties: { permitId, permitType, hazardClass, highRisk }.",
    category: "funnel",
    funnelSlug: "ptw_lifecycle",
    funnelStep: 4,
  },
  {
    key: "ptw.report.permit_expired",
    label: "PTW report — permit expired",
    description:
      "Permit hit its issued expiry window without being renewed/closed. Properties: { permitId, permitType, hazardClass, expiredFromStatus }. Drives the expiry-funnel widget on the operator dashboard.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_expiry_warning",
    label: "PTW report — permit nearing expiry",
    description:
      "Permit crossed the expiry-warning threshold (default 24h before issuedExpiryTime). Properties: { permitId, permitType, hoursToExpiry }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_suspended",
    label: "PTW report — permit suspended",
    description:
      "Permit was suspended. Properties: { permitId, permitType, suspensionReason: 'manual'|'gas_overdue'|'incident_cascade'|'emergency_stop'|'other' }. Drives the suspension-reasons widget.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_resumed",
    label: "PTW report — permit resumed",
    description:
      "A previously suspended permit was restarted. Properties: { permitId, permitType, suspendedDurationMs }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_returned",
    label: "PTW report — permit step returned for refill",
    description:
      "A permit step was returned to a previous step for rework. Properties: { permitId, permitType, returnedCause: 'incomplete_data'|'gas_test_required'|'isolation_required'|'simops_conflict'|'documentation'|'other' }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.permit_extended",
    label: "PTW report — permit extended",
    description:
      "A permit's expiry was extended via amendment. Properties: { permitId, permitType, extensionMs, newExpiry }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.emergency_stop",
    label: "PTW report — emergency stop",
    description:
      "Emergency stop was applied to an active/issued permit. Properties: { permitId, permitType, hazardClass, secondSignerRole }. Broadcast severity drives the HSE dashboard's incident overlay.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.approval_step_completed",
    label: "PTW report — approval step completed",
    description:
      "An approval/review/issue step was completed. Properties: { permitId, permitType, stepType, elapsedMs, slaBreached }. Drives the approval-SLA distribution widget.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.approval_sla_breach",
    label: "PTW report — approval SLA breached",
    description:
      "An approval/issue/review step exceeded its SLA target. Properties: { permitId, permitType, stepType, elapsedMs, slaTargetMs }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.gas_retest_failed",
    label: "PTW report — gas retest failed",
    description:
      "A gas retest was logged with a failing reading (or could not be completed). Properties: { permitId, permitType, gasType?, recordedBy? }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.gas_retest_overdue",
    label: "PTW report — gas retest overdue (auto-suspend)",
    description:
      "A scheduled gas retest passed its `next_due_at` and the enforcer auto-suspended the permit. Properties: { permitId, permitType, overdueMs }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.equipment_gate_failed",
    label: "PTW report — equipment gate failed",
    description:
      "Equipment-readiness gate evaluation returned a failure for a permit. Properties: { permitId, permitType, gate, reason }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.high_risk_active",
    label: "PTW report — high-risk permit active",
    description:
      "A high-risk hazard-class permit (hot_work, confined_space, energized_electrical, etc.) transitioned to active. Properties: { permitId, permitType, hazardClass, siteId? }. Drives the high-risk density heatmap.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.cap_utilization_sample",
    label: "PTW report — capacity utilization sample",
    description:
      "Periodic snapshot of concurrent-permit capacity utilization for a site. Properties: { siteId?, hazardClass, active, capLimit, utilizationPct }. Sampled by the analytics tick — not per-permit.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.manual_fallback_used",
    label: "PTW report — manual fallback invoked",
    description:
      "A PTW gate fell through to manual attestation (integration unavailable or feature disabled). Properties: { permitId?, permitType?, gate, fallbackReason }.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.alias_hit",
    label: "PTW report — legacy alias surface hit",
    description:
      "A request hit a legacy PTW URL alias (`/api/ptw-v2/*` shim or `/equipment/ptw-panel`-style client redirect). Properties: { surfaceKind: 'server_legacy_v1'|'server_v2_to_v1'|'client_redirect', alias, userAgentClass }. Mirrors the `nashid_ptw_alias_hits_total` Prometheus counter for tenant-scoped dashboards.",
    category: "lifecycle",
  },
  {
    key: "ptw.report.flag_evaluated",
    label: "PTW report — PTW-scoped feature flag evaluated",
    description:
      "A PTW-namespace feature flag (`operations.ptw.*` entitlement or Unleash rollout) was evaluated in a PTW request path. Properties: { flag, enabled, source }. Permits PTW-scoped flag adoption tracking without scanning the full `feature.flag_evaluated` stream.",
    category: "feature",
  },

  // ─── funnel: muster response ──────────────────────────────────────
  {
    key: "muster.activated",
    label: "Muster activated",
    description: "An emergency muster event was activated for a site.",
    category: "funnel",
    funnelSlug: "muster_response",
    funnelStep: 1,
  },
  {
    key: "muster.first_response",
    label: "Muster first response received",
    description: "First worker check-in arrived for an active muster.",
    category: "funnel",
    funnelSlug: "muster_response",
    funnelStep: 2,
  },
  {
    key: "muster.fully_accounted",
    label: "Muster fully accounted",
    description: "Every worker on site has checked in or been resolved.",
    category: "funnel",
    funnelSlug: "muster_response",
    funnelStep: 3,
  },
];

export const TELEMETRY_FUNNELS: TelemetryFunnelDef[] = [
  {
    slug: "worker_onboarding",
    label: "Worker onboarding completion",
    steps: [
      { eventKey: "onboarding.started", label: "Started" },
      { eventKey: "onboarding.identity_verified", label: "Identity verified" },
      { eventKey: "onboarding.documents_uploaded", label: "Documents uploaded" },
      { eventKey: "onboarding.completed", label: "Completed" },
    ],
  },
  {
    slug: "permit_creation",
    label: "Permit creation",
    steps: [
      { eventKey: "permit.create_started", label: "Started" },
      { eventKey: "permit.create_template_selected", label: "Template selected" },
      { eventKey: "permit.create_submitted", label: "Submitted" },
      { eventKey: "permit.create_issued", label: "Issued" },
    ],
  },
  {
    slug: "muster_response",
    label: "Muster response",
    steps: [
      { eventKey: "muster.activated", label: "Activated" },
      { eventKey: "muster.first_response", label: "First response" },
      { eventKey: "muster.fully_accounted", label: "Fully accounted" },
    ],
  },
  // PTW Phase 11.A (Task #1290) — full lifecycle funnel from creation
  // through closure. Drives the operator dashboard's expiry-funnel
  // widget plus the leadership dashboard's permit-throughput trend.
  {
    slug: "ptw_lifecycle",
    label: "PTW permit lifecycle",
    steps: [
      { eventKey: "ptw.report.permit_created", label: "Created" },
      { eventKey: "ptw.report.permit_submitted", label: "Submitted" },
      { eventKey: "ptw.report.permit_issued", label: "Issued" },
      { eventKey: "ptw.report.permit_active", label: "Active" },
      { eventKey: "ptw.report.permit_closed", label: "Closed" },
    ],
  },
];

export const TELEMETRY_EVENT_BY_KEY: Readonly<Record<string, TelemetryEventDef>> =
  Object.freeze(
    Object.fromEntries(TELEMETRY_EVENT_TAXONOMY.map((e) => [e.key, e])),
  );

export type TelemetryEventKey = (typeof TELEMETRY_EVENT_TAXONOMY)[number]["key"];

export const TELEMETRY_EVENT_KEYS: ReadonlySet<string> = new Set(
  TELEMETRY_EVENT_TAXONOMY.map((e) => e.key),
);

/**
 * Coarse role bucket. We never persist the literal role string from the
 * users / memberships table because some tenants define custom roles
 * whose names are PII-adjacent (e.g. "Acme HR Manager — Sarah").
 */
export type TelemetryRoleBucket =
  | "super_admin"
  | "tenant_admin"
  | "worker"
  | "partner"
  | "viewer"
  | "other";

const SUPER_ADMIN_HINTS = ["super_admin", "superadmin", "platform_admin"];
const TENANT_ADMIN_HINTS = ["tenant_admin", "site_manager", "hr_manager", "ops_lead", "hse_officer"];
const WORKER_HINTS = ["worker"];
const PARTNER_HINTS = ["partner"];
const VIEWER_HINTS = ["viewer", "guest"];

/** Map an arbitrary role string to a stable, low-cardinality bucket. */
export function roleBucketFromRole(role: string | null | undefined): TelemetryRoleBucket | null {
  if (!role) return null;
  const normalised = role.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (SUPER_ADMIN_HINTS.some((h) => normalised.includes(h))) return "super_admin";
  if (TENANT_ADMIN_HINTS.some((h) => normalised.includes(h))) return "tenant_admin";
  if (WORKER_HINTS.some((h) => normalised.includes(h))) return "worker";
  if (PARTNER_HINTS.some((h) => normalised.includes(h))) return "partner";
  if (VIEWER_HINTS.some((h) => normalised.includes(h))) return "viewer";
  return "other";
}

const TELEMETRY_ROLE_BUCKETS: ReadonlySet<TelemetryRoleBucket> = new Set([
  "super_admin",
  "tenant_admin",
  "worker",
  "partner",
  "viewer",
  "other",
]);

export function isTelemetryRoleBucket(value: unknown): value is TelemetryRoleBucket {
  return typeof value === "string" && TELEMETRY_ROLE_BUCKETS.has(value as TelemetryRoleBucket);
}

/**
 * Strip route parameters that look like opaque IDs so the surface field
 * stays low-cardinality. Replaces UUIDs and 8+ digit numeric IDs with
 * `:id`. Strips query string and fragment entirely.
 */
export function normaliseSurface(rawPath: string): string {
  if (typeof rawPath !== "string" || rawPath.length === 0) return "/";
  // Strip query + hash.
  const hashIdx = rawPath.indexOf("#");
  const queryIdx = rawPath.indexOf("?");
  const cutIdx = [hashIdx, queryIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const pathOnly = cutIdx == null ? rawPath : rawPath.slice(0, cutIdx);
  // Replace UUIDs.
  const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  let normalised = pathOnly.replace(uuidRe, ":id");
  // Replace long numeric IDs (>=4 digits, conservative — keeps `/v2` etc).
  normalised = normalised.replace(/\/\d{4,}/g, "/:id");
  return normalised || "/";
}

/**
 * PII allow-list for the `properties` payload. Keeps payloads opaque by
 * dropping anything whose value is a string longer than 200 chars (a
 * conservative proxy for "free text") and anything whose key looks like
 * it might be PII.
 */
const PII_KEY_DENYLIST = new Set([
  "email",
  "phone",
  "name",
  "fullname",
  "first_name",
  "last_name",
  "user_id",
  "userid",
  "username",
  "address",
  "ip",
  "ip_address",
  "national_id",
  "passport",
  "national_number",
  "ssn",
]);

export function scrubProperties(input: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!input) return {};
  const out: Record<string, unknown> = {};
  for (const [rawKey, value] of Object.entries(input)) {
    const key = rawKey.toLowerCase();
    if (PII_KEY_DENYLIST.has(key)) continue;
    if (PII_KEY_DENYLIST.has(key.replace(/[^a-z]/g, ""))) continue;
    if (value === null || value === undefined) {
      out[rawKey] = null;
      continue;
    }
    if (typeof value === "string") {
      // Drop overly long free-text values.
      if (value.length > 200) continue;
      out[rawKey] = value;
      continue;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      out[rawKey] = value;
      continue;
    }
    // Drop nested objects / arrays — we never need them for product
    // analytics and they are the most common PII vector.
  }
  return out;
}
